import { createContext, useContext, useState, ReactNode, useEffect } from "react";
import { fetchSettings, saveSettings as apiSaveSettings } from "../lib/api";

export interface Prize {
  id: number;
  tag: string;
  tagColor: string;
  title: string;
  value: string;
  features: string[]; // Still keep for simple display
  img: string;
  description: string;
  gallery: string[];
  specifications: { label: string; value: string; icon: string }[];
}

export interface HowItWorksStep {
  num: string;
  icon: string;
  title: string;
  desc: string;
}

export interface Charity {
  icon: string;
  category: string;
  org: string;
  desc: string;
  amount: string;
}

export interface PastDraw {
  id: string;
  date: string;
  prize: string;
  prizeValue: string;
  winningNumber: string;
  winner: string;
  participants: number;
  status: "done" | "upcoming";
}

export interface SiteSettings {
  drawId: string;
  siteName: string;
  lotteryValue: string;
  nextDrawDate: string;
  drawClosingDate: string;
  bailiffName: string;
  maintenanceMode: boolean;
  registrationOpen: boolean;
  alertText: string;
  companyName: string;
  contactAddress: string;
  contactPhone: string;
  contactEmail: string;
  supportEmail: string;
  logo: string;
  favicon: string;
  metaDescription: string;
  metaKeywords: string;
  termsOfService: string;
  privacyPolicy: string;
  pastDraws: PastDraw[];
  prizes: Prize[];
  howItWorksSteps: HowItWorksStep[];
  charities: Charity[];
  charityStats: { label: string; value: string }[];
  testimonials: { name: string; content: string; rating: number }[];
  packages: { name: string; count: number; price: string; popular?: boolean }[];
  socials: { title: string; href: string }[];
  paymentProviders: {
    stripeEnabled: boolean;
    stripePublicKey: string;
    stripeSecretKey: string;
    paypalEnabled: boolean;
    paypalClientId: string;
    paypalSecretKey: string;
    bankTransferEnabled: boolean;
    bankIban: string;
    bankBic: string;
    bankHolder: string;
  };
  propertyTitle: string;
  propertyLocation: string;
  propertyDescription: string;
  propertySurface: string;
  propertyLand: string;
  propertyRooms: string;
  propertyBathrooms: string;
  propertyHighlights: string[];
  partners: string[];
  propertyImages: {
    hero: string;
    showcaseExterior: string;
    showcaseInterior: string;
    showcaseLand: string;
    gallery: string[];
  };
  chatEnabled: boolean;
  chatBotName: string;
  chatWelcomeMessage: string;
  chatResponseTime: string;
  thirdPartyChatScript: string;
  adminEmail: string;
  adminPassword: string;
  lifestyleSlides: {
    img: string;
    label: string;
    badgeTitle: string;
    badgeSub: string;
  }[];
}

interface AdminContextProps {
  settings: SiteSettings;
  updateSettings: (newSettings: Partial<SiteSettings>) => void;
  saveSettings: () => Promise<void>;
  loading: boolean;
}

const DEFAULT_SETTINGS: SiteSettings = {
  drawId: "#52",
  siteName: "German Traumhaus Lotterie",
  lotteryValue: "1 250 000 €",
  nextDrawDate: "2026-03-25",
  drawClosingDate: "2026-03-24",
  bailiffName: "Dr. Hans-Peter Schmidt",
  alertText: "🔥 NUR NOCH 4 TAGE BIS ZUR VILLA-ZIEHUNG! NUTZEN SIE -10% AUF ALLE PAKETE MIT DEM CODE GEWINNER10 🔥",
  maintenanceMode: false,
  registrationOpen: true,
  companyName: "Dreamify gGmbH",
  contactAddress: "Jungfernstieg 47, 20354 Hamburg, Deutschland",
  contactPhone: "+49 (0) 211 1234 567",
  contactEmail: "info@germantraumhauslotterie.de",
  supportEmail: "info@germantraumhauslotterie.de",
  logo: "https://readdy.ai/api/search-image?query=luxury%20modern%20gold%20and%20black%20infinity%20logo%2C%20minimalist%20sleek%20design%2C%20high%20end%20branding%2C%20transparent%20background&width=200&height=50&seq=logo-1",
  favicon: "/favicon.ico",
  metaDescription: "Gewinnen Sie die Villa Ihrer Träume mit der German Traumhaus Lotterie. Nehmen Sie an der größten zertifizierten Immobilien-Lotterie teil.",
  metaKeywords: "Lotterie, Villa, Haus, gewinnen, Verlosung, Immobilien",
  termsOfService: "# Allgemeine Geschäftsbedingungen\n\nWillkommen bei der German Traumhaus Lotterie...",
  privacyPolicy: "# Datenschutzbestimmungen\n\nWir schützen Ihre Daten...",
  pastDraws: [
    { id: "51", date: "15. Januar 2026", prize: "Loft-Apartment Berlin", prizeValue: "450.000 €", winningNumber: "2 8 4 1 5", winner: "Markus D.", participants: 12450, status: "done" },
    { id: "50", date: "12. Dezember 2025", prize: "Villa Teneriffa", prizeValue: "780.000 €", winningNumber: "9 1 3 0 7", winner: "Elena R.", participants: 15600, status: "done" },
    { id: "49", date: "10. November 2025", prize: "Mercedes GT 63 S", prizeValue: "185.000 €", winningNumber: "0 5 2 9 8", winner: "Thomas K.", participants: 8900, status: "done" },
  ],
  prizes: [
    {
      id: 1,
      tag: "HAUPTPREIS",
      tagColor: "bg-primary",
      title: "Moderne Villa 500 m²",
      value: "1.250.000 €",
      features: ["5 Zimmer, 3 Badezimmer", "Privater Pool & Spa", "1.200 m² Garten, Doppelgarage"],
      img: "https://readdy.ai/api/search-image?query=ultra%20luxurious%20modern%20white%20villa%20with%20infinity%20swimming%20pool%2C%20expansive%20terrace%2C%20manicured%20landscaping%2C%20architectural%20masterpiece%20with%20large%20glass%20panels%2C%20blue%20sky%20background%2C%20professional%20real%20estate%20photography%2C%20Mediterranean%20estate%2C%20stone%20and%20glass%20facade%2C%20premium%20location%20hillside&width=800&height=600&seq=prize-villa-1&orientation=landscape",
      description: "Eine Architektenvilla mit unvergleichlichem Luxus und atemberaubendem Blick auf das Meer...",
      gallery: [],
      specifications: [
        { label: "Wohnfläche", value: "500 m²", icon: "ri-ruler-2-line" },
        { label: "Zimmer", value: "5", icon: "ri-hotel-bed-line" },
        { label: "Grundstück", value: "1.200 m²", icon: "ri-tree-line" }
      ]
    },
    {
      id: 2,
      tag: "2. PREIS",
      tagColor: "bg-sky-500",
      title: "Premium Elektro-SUV",
      value: "98.500 €",
      features: ["Tesla Model X Long Range", "Reichweite 580 km"],
      img: "https://readdy.ai/api/search-image?query=sleek%20black%20Tesla%20Model%20X%20SUV%20parked%20on%20scenic%20mountain%20road%2C%20electric%20vehicle%2C%20side%20angle%20view%2C%20dramatic%20landscape%20background%2C%20luxury%20automotive%20photography%2C%20premium%20electric%20car%2C%20moody%20lighting%2C%20professional%20photo&width=600&height=400&seq=prize-car-1&orientation=landscape",
      description: "Erleben Sie die Zukunft des Fahrens mit maximalem Stil...",
      gallery: [],
      specifications: [
        { label: "Modell", value: "Tesla Model X", icon: "ri-car-line" },
        { label: "Reichweite", value: "580 km", icon: "ri-battery-charge-line" },
        { label: "0-100 km/h", value: "3.9s", icon: "ri-flashlight-line" }
      ]
    },
    {
      id: 3,
      tag: "3. PREIS",
      tagColor: "bg-secondary",
      title: "Luxus-Weltreise",
      value: "45.000 €",
      features: ["2 Personen, 30 Tage", "Inklusive 5-Sterne-Hotels"],
      img: "https://readdy.ai/api/search-image?query=stunning%20luxury%20overwater%20bungalow%20resort%20in%20Maldives%2C%20crystal%20clear%20turquoise%20lagoon%2C%20white%20sand%20beach%2C%20palm%20trees%2C%20paradise%20island%20vacation%2C%20aerial%20view%2C%20sunset%20golden%20light%2C%20tropical%20paradise%20destination%2C%20five%20star%20resort&width=600&height=400&seq=prize-travel-1&orientation=landscape",
      description: "Die Reise Ihres Lebens durch die exklusivsten Resorts...",
      gallery: [],
      specifications: [
        { label: "Personen", value: "2", icon: "ri-group-line" },
        { label: "Dauer", value: "30 Tage", icon: "ri-calendar-line" },
        { label: "Hotels", value: "5 Sterne", icon: "ri-hotel-line" }
      ]
    },
  ],
  howItWorksSteps: [
    { num: "01", icon: "ri-ticket-2-line", title: "Paket wählen", desc: "Wählen Sie die Anzahl der Lose, die zu Ihrem Budget passt. Mehr Lose bedeuten höhere Gewinnchancen!" },
    { num: "02", icon: "ri-shield-check-line", title: "Sicher bezahlen", desc: "Zahlen Sie per Kreditkarte, PayPal oder Überweisung. Ihre Zahlung ist SSL-verschlüsselt." },
    { num: "03", icon: "ri-order-play-line", title: "Lose erhalten", desc: "Innerhalb von 2 Minuten nach Ihrer Zahlung erhalten Sie Ihre Teilnahmenummern per E-Mail." },
    { num: "04", icon: "ri-live-line", title: "Ziehung verfolgen", desc: "Die Live-Ziehung wird notariell beglaubigt. Wir benachrichtigen Sie sofort bei einem Gewinn!" },
  ],
  charities: [
    { icon: "ri-plant-line", category: "Umwelt & Natur", org: "NABU Deutschland", desc: "Artenschutz und Schutz natürlicher Ökosysteme in Deutschland — Wälder, Feuchtgebiete, Biodiversität.", amount: "38.400 €" },
    { icon: "ri-home-heart-line", category: "Soziale Hilfe & Kinder", org: "SOS Kinderdorf", desc: "Unterstützung von Kindern und Familien in schutzbedürftigen Situationen im ganzen Land.", amount: "52.200 €" },
  ],
  charityStats: [
    { value: "10 %", label: "Von jedem Los gespendet" },
    { value: "142.150 €", label: "In 2024 bereits gespendet" },
    { value: "12", label: "Unterstützte Organisationen" },
    { value: "100 %", label: "Garantierte Transparenz" },
  ],
  testimonials: [
    { name: "Markus D.", content: "Ich habe bei der letzten Ziehung 50€ gewonnen, jetzt hoffe ich auf die Villa!", rating: 5 },
    { name: "Sophie L.", content: "Zahlung und Ablauf waren super einfach und sicher. Top!", rating: 4 },
  ],
  packages: [
    { name: "Pack 5 tickets", count: 5, price: "22€" },
    { name: "Pack 25 + 2 gratis", count: 27, price: "90€", popular: true },
    { name: "Pack 50 + 8 gratis", count: 58, price: "150€" },
  ],
  socials: [
    { title: "Instagram", href: "https://www.instagram.com/germantraumhauslotterie/" },
    { title: "Facebook", href: "https://www.facebook.com/germantraumhauslotterie/" },
  ],
  chatEnabled: true,
  chatBotName: "Traumhaus Support",
  chatWelcomeMessage: "Hallo! Ich bin der German Traumhaus Lotterie-Assistent. Wie kann ich Ihnen heute helfen?",
  chatResponseTime: "Antwortet in ~45 Min",
  thirdPartyChatScript: "",
  paymentProviders: {
    stripeEnabled: true,
    stripePublicKey: "",
    stripeSecretKey: "",
    paypalEnabled: true,
    paypalClientId: "",
    paypalSecretKey: "",
    bankTransferEnabled: true,
    bankIban: "DE62 1234 5678 9012 3456 00",
    bankBic: "GENODED1",
    bankHolder: "Dreamify gGmbH",
  },
  propertyTitle: "Exklusive Villa am Starnberger See",
  propertyLocation: "Seeshaupt · Deutschland",
  propertyDescription: "Eine außergewöhnliche Luxusvilla in Bestlage am Starnberger See. Absolute Privatsphäre, modernste Architektur und hochwertigste Ausstattung...",
  propertySurface: "500",
  propertyLand: "1.200",
  propertyRooms: "5",
  propertyBathrooms: "3",
  propertyHighlights: ["See- & Bergblick", "Privater Wellnessbereich", "Smart-Home Vollausstattung"],
  partners: ["Focus", "Süddeutsche Zeitung", "WirtschaftsWoche"],
  propertyImages: {
    hero: "https://readdy.ai/api/search-image?query=ultra%20luxurious%20modern%20white%20villa%20with%20infinity%20swimming%20pool%2C%20expansive%20terrace%2C%20manicured%20landscaping%2C%20architectural%20masterpiece%20with%20large%20glass%20panels%2C%20blue%20sky%20background%2C%20professional%20real%20estate%20photography%2C%20Mediterranean%20estate%2C%20stone%20and%20glass%20facade%2C%20premium%20location%20hillside&width=1600&height=1000&seq=hero-villa-1",
    showcaseExterior: "https://readdy.ai/api/search-image?query=modern%20villa%20exterior%20daylight&width=800&height=600&seq=ext-1",
    showcaseInterior: "https://readdy.ai/api/search-image?query=modern%20luxury%20living%20room%20interior&width=800&height=600&seq=int-1",
    showcaseLand: "https://readdy.ai/api/search-image?query=luxury%20villa%20garden%20landscaping&width=800&height=600&seq=land-1",
    gallery: [],
  },
  adminEmail: "info@germantraumhauslotterie.de",
  adminPassword: "admin",
  lifestyleSlides: [
    {
      img: "https://readdy.ai/api/search-image?query=stunning%20modern%20luxury%20villa%20exterior%20with%20large%20swimming%20pool%2C%20palm%20trees%2C%20mediterranean%20architecture%2C%20white%20walls%2C%20blue%20sky%2C%20manicured%20garden%2C%20high-end%20real%20estate%20photography%2C%20warm%20golden%20hour%20light%2C%20ultra%20sharp%20clean%20minimal%20background&width=800&height=1000&seq=lifestyle-slide-1&orientation=portrait",
      label: "Das Traumhaus",
      badgeTitle: "Villa Föhr",
      badgeSub: "Geschätzter Wert · 1.250.000 €",
    },
    {
      img: "https://readdy.ai/api/search-image?query=luxurious%20bright%20open%20plan%20living%20room%20interior%20with%20floor%20to%20ceiling%20windows%2C%20designer%20furniture%2C%20marble%20floors%2C%20large%20terrace%20view%2C%20elegant%20minimal%20modern%20style%2C%20soft%20natural%20light%2C%20ultra%20clean%20photography%2C%20no%20people%2C%20real%20estate&width=800&height=1000&seq=lifestyle-slide-2&orientation=portrait",
      label: "Interieur",
      badgeTitle: "Luxus-Interieur",
      badgeSub: "450 m² · 8 Zimmer",
    },
    {
      img: "https://readdy.ai/api/search-image?query=beautiful%20mediterranean%20villa%20infinity%20pool%20overlooking%20sea%2C%20mallorca%20spain%2C%20luxury%20outdoor%20terrace%20lounge%2C%20bougainvillea%2C%20sunset%20golden%20light%2C%20stunning%20holiday%20dream%20home%2C%20real%20estate%20photography%20ultra%20sharp&width=800&height=1000&seq=lifestyle-slide-3&orientation=portrait",
      label: "Zweitwohnsitz",
      badgeTitle: "Villa Mallorca",
      badgeSub: "Bonus-Ziehung · 850.000 €",
    },
    {
      img: "https://readdy.ai/api/search-image?query=modern%20luxury%20kitchen%20with%20marble%20island%2C%20high%20end%20appliances%2C%20pendant%20lights%2C%20open%20plan%20dining%20area%2C%20white%20and%20wood%20tones%2C%20very%20bright%20airy%20contemporary%20interior%20design%2C%20no%20people%2C%20professional%20real%20estate%20photography&width=800&height=1000&seq=lifestyle-slide-4&orientation=portrait",
      label: "Wohnbereich",
      badgeTitle: "Einbauküche",
      badgeSub: "Ausstattung · 45.000 €",
    },
    {
      img: "https://readdy.ai/api/search-image?query=luxurious%20master%20bedroom%20with%20panoramic%20sea%20view%2C%20large%20windows%2C%20king%20bed%2C%20elegant%20linen%2C%20minimalist%20design%2C%20warm%20neutral%20tones%2C%20soft%20morning%20light%2C%20premium%20real%20estate%20photography%2C%20clean%20serene%20atmosphere&width=800&height=1000&seq=lifestyle-slide-5&orientation=portrait",
      label: "Master-Suite",
      badgeTitle: "Eltern-Suite",
      badgeSub: "Meerblick · Ankleide",
    },
  ],
};

const AdminContext = createContext<AdminContextProps | undefined>(undefined);

export function AdminProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<SiteSettings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        let data = await fetchSettings();
        if (data && Object.keys(data).length > 0) {
          // AUTO-MIGRATION: If detected French steps, replace with German defaults
          if (data.howItWorksSteps && Array.isArray(data.howItWorksSteps)) {
            const firstStepTitle = data.howItWorksSteps[0]?.title;
            if (firstStepTitle === "Choisissez votre forfait") {
              data.howItWorksSteps = DEFAULT_SETTINGS.howItWorksSteps;
            }
          }

          // Migrate Charity if French
          if (data.charities && Array.isArray(data.charities)) {
            const firstCharityCat = data.charities[0]?.category;
            if (firstCharityCat === "Environnement & Nature") {
              data.charities = DEFAULT_SETTINGS.charities;
            }
          }

          // Migrate Charity Stats if French
          if (data.charityStats && Array.isArray(data.charityStats)) {
            const firstStatLabel = data.charityStats[0]?.label;
            if (firstStatLabel === "Donné sur chaque ticket") {
              data.charityStats = DEFAULT_SETTINGS.charityStats;
            }
          }

          // Migrate Prizes if French
          if (data.prizes && Array.isArray(data.prizes)) {
            const firstPrizeTitle = data.prizes[0]?.title;
            if (firstPrizeTitle === "Villa Moderne 500 m²") {
              data.prizes = DEFAULT_SETTINGS.prizes;
            }
          }
          // Merge defaults with saved data to avoid missing fields if schema changes
          setSettings(prev => ({ ...prev, ...data }));
        }
      } catch (err) {
        console.error("Settings load failed:", err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const updateSettings = (newSettings: Partial<SiteSettings>) => {
    setSettings(prev => ({ ...prev, ...newSettings }));
  };

  const saveSettings = async () => {
    try {
      await apiSaveSettings(settings);
    } catch (err) {
      console.error("Settings save failed:", err);
      throw err;
    }
  };

  return (
    <AdminContext.Provider value={{ settings, updateSettings, saveSettings, loading }}>
      {children}
    </AdminContext.Provider>
  );
}

export function useAdmin() {
  const context = useContext(AdminContext);
  if (context === undefined) {
    throw new Error("useAdmin must be used within an AdminProvider");
  }
  return context;
}
